import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalOverlay, ModalContent, ModalCloseButton } from "@chakra-ui/react";
import { api } from "../../lib/api";

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ink-400 mb-1 px-1">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function highlight(text, q) {
  if (!text || !q) return text || "";
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="bg-yellow-200 px-0.5 rounded">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function snippet(html, q) {
  const text = (html || "").replace(/<[^>]+>/g, "");
  if (text.length < 100) return text;
  const i = text.toLowerCase().indexOf((q || "").toLowerCase());
  if (i < 0) return text.slice(0, 90) + "...";
  const start = Math.max(0, i - 30);
  return (start > 0 ? "..." : "") + text.slice(start, start + 100) + "...";
}

export default function ChatSearchModal({ isOpen, onClose, onSelectChannel }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ messages: [], users: [], files: [], projects: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setQ(""); setResults({ messages: [], users: [], files: [], projects: [] });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setResults({ messages: [], users: [], files: [], projects: [] }); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await api(`/chat/search/global?q=${encodeURIComponent(q)}`);
        setResults({
          messages: r.messages || [],
          users: r.users || [],
          files: r.files || [],
          projects: r.projects || [],
        });
      } catch (e) { setResults({ messages: [], users: [], files: [], projects: [] }); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  function go(msg) {
    onClose();
    onSelectChannel && onSelectChannel(msg.channel);
  }

  const totalHits =
    (results.messages?.length || 0) +
    (results.users?.length || 0) +
    (results.files?.length || 0) +
    (results.projects?.length || 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalCloseButton />
        <div className="p-5 max-h-[80vh] flex flex-col">
          <h2 className="text-base font-semibold text-ink-800 mb-3">Search messages</h2>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search messages, polls, names…"
            className="w-full text-sm border border-ink-200 rounded px-3 py-2 outline-none focus:border-blue-300 mb-3"
          />
          <div className="flex-1 overflow-y-auto space-y-4">
            {loading && <div className="text-center text-xs text-ink-400 py-3">Searching…</div>}
            {!loading && q.trim().length >= 2 && totalHits === 0 && (
              <div className="text-center text-xs text-ink-400 py-6">No matches</div>
            )}
            {!loading && q.trim().length < 2 && (
              <div className="text-center text-xs text-ink-400 py-6">Type at least 2 characters</div>
            )}

            {results.users?.length > 0 && (
              <Section title={`People (${results.users.length})`}>
                {results.users.map((u) => (
                  <a key={u._id} href={`/users/${u._id}`} className="block px-3 py-2 rounded hover:bg-blue-50/40 border border-ink-100">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-ink-200 text-ink-600 text-[10px] font-semibold flex items-center justify-center">
                        {(u.username || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm text-ink-800">{highlight(u.username, q)}</div>
                        <div className="text-[10px] text-ink-400">{u.designation || u.email}</div>
                      </div>
                    </div>
                  </a>
                ))}
              </Section>
            )}

            {results.projects?.length > 0 && (
              <Section title={`Projects (${results.projects.length})`}>
                {results.projects.map((p) => (
                  <a key={p._id} href={`/projects/${p._id}`} className="block px-3 py-2 rounded hover:bg-blue-50/40 border border-ink-100">
                    <div className="flex items-center gap-2">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || "#999" }} />
                      <div className="text-sm text-ink-800">{highlight(p.name, q)}</div>
                      <span className="text-[10px] text-ink-400 ml-auto">{p.key}</span>
                    </div>
                  </a>
                ))}
              </Section>
            )}

            {results.files?.length > 0 && (
              <Section title={`Files (${results.files.length})`}>
                {results.files.map((f) => (
                  <div key={f._id} className="px-3 py-2 rounded border border-ink-100">
                    <div className="text-sm text-ink-800">{highlight(f.name, q)}</div>
                    <div className="text-[10px] text-ink-400">{f.mime} · {new Date(f.createdAt).toLocaleDateString()}</div>
                  </div>
                ))}
              </Section>
            )}

            {results.messages?.length > 0 && (
              <Section title={`Messages (${results.messages.length})`}>
                {results.messages.map((m) => {
                  const ch = m.channelInfo || {};
                  const chLabel =
                    ch.kind === "general" ? "# general" :
                    ch.kind === "dept" ? `# ${ch.name}` :
                    ch.kind === "project" ? `# ${ch.project?.name || ch.name || "project"}` :
                    ch.kind === "task" ? `# task` :
                    ch.kind === "dm" ? "DM" :
                    ch.name || "channel";
                  const previewText = m.kind === "poll" ? `📊 ${m.poll?.question || "Poll"}` : snippet(m.text, q);
                  return (
                    <button key={m._id} onClick={() => go(m)} className="w-full p-3 text-left rounded border border-ink-100 hover:border-blue-300 hover:bg-blue-50/40 transition">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-blue-700">{chLabel}</span>
                        <span className="text-[10px] text-ink-400">{new Date(m.createdAt).toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-ink-500 mb-1">{m.author?.username || "—"}</div>
                      <div className="text-sm text-ink-800">{highlight(previewText, q)}</div>
                    </button>
                  );
                })}
              </Section>
            )}
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
